import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import axios from "axios";
import "../styles/Orders.css";

type OrderTab =
  | "all"
  | "to_pay"
  | "to_ship"
  | "to_receive"
  | "completed"
  | "cancelled"
  | "return_refund";

interface OrderItem {
  id: number;
  orderItemId: number;
  barCode?: string;
  productName: string;
  image_path?: string;
  variant?: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface Order {
  id: number;
  orderId: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  totalAmount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  createdAt: string;
  updatedAt: string;
  items: OrderItem[];
  status?: OrderTab;
}

interface ReviewFormState {
  rating: number;
  comment: string;
  photos: File[];
  videos: File[];
}

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { addMultipleToCart } = useCart();
  const { showModal } = useMessageModal();
  const [activeTab, setActiveTab] = useState<OrderTab>("all");
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoadingOrderId, setCancelLoadingOrderId] = useState<
    string | null
  >(null);

  const [expandedReviewOrderId, setExpandedReviewOrderId] = useState<
    string | null
  >(null);
  const [reviewForms, setReviewForms] = useState<
    Record<string, ReviewFormState>
  >({});
  const [reviewSubmittingOrderId, setReviewSubmittingOrderId] = useState<
    number | null
  >(null);

  const currentUser = JSON.parse(
    localStorage.getItem("subscriber_user") || "{}",
  );
  const buyerEmail = currentUser?.email || localStorage.getItem("email") || "";

  const canCancelOrder = (order: any) => {
    const status = String(order.status || "").toLowerCase();
    return status === "to_pay" || status === "to_ship";
  };

  const resolveOrderStatus = (order: any): OrderTab => {
    const backendStatus = String(order.orderStatus || "").toLowerCase();

    switch (backendStatus) {
      case "cancelled":
        return "cancelled";
      case "completed":
        return "completed";
      case "return_refund":
        return "return_refund";
      case "to_receive":
      case "shipped":
        return "to_receive";
      case "to_ship":
      case "paid":
      case "processing":
        return "to_ship";
      case "to_pay":
        return "to_pay";
      case "pending":
      default:
        return order.paymentMethod !== "Cash on Delivery"
          ? "to_ship"
          : "to_pay";
    }
  };

  const fetchOrders = async () => {
    try {
      if (!buyerEmail) {
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/orders`,
        {
          params: { buyerEmail },
        },
      );

      const mappedOrders = (response.data?.data || []).map((order: any) => ({
        ...order,
        status: resolveOrderStatus(order),
      }));

      setOrders(mappedOrders);
    } catch (error) {
      console.error("fetchOrders error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [buyerEmail]);

  const handleBuyAgain = (order: any) => {
    if (!order?.items?.length) return;

    const productsToAdd = order.items.map((item: any) => ({
      id: item.id,
      barCode: item.barCode || "",
      productName: item.productName,
      active_price: item.unitPrice,
      image_path: item.image_path || "",
      quantity: Number(item.quantity || 1),
    }));

    addMultipleToCart(productsToAdd);
    navigate("/shopping-cart");
  };

  const getDefaultReviewForm = (): ReviewFormState => ({
    rating: 5,
    comment: "",
    photos: [],
    videos: [],
  });

  const handleReview = (order: any) => {
    setExpandedReviewOrderId((prev) =>
      prev === order.orderId ? null : order.orderId,
    );

    setReviewForms((prev) => ({
      ...prev,
      [order.orderId]: prev[order.orderId] || getDefaultReviewForm(),
    }));
  };

  const updateReviewForm = (
    orderId: string,
    field: keyof ReviewFormState,
    value: any,
  ) => {
    setReviewForms((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || getDefaultReviewForm()),
        [field]: value,
      },
    }));
  };

  const handlePhotoChange = (orderId: string, files: FileList | null) => {
    if (!files) return;
    updateReviewForm(orderId, "photos", Array.from(files));
  };

  const handleVideoChange = (orderId: string, files: FileList | null) => {
    if (!files) return;
    updateReviewForm(orderId, "videos", Array.from(files));
  };

  const handleSubmitReview = async (order: Order) => {
    const form = reviewForms[order.orderId] || getDefaultReviewForm();

    if (!form.rating) {
      showModal({
        type: "confirm",
        title: "confirm",
        message: "Please select rating.",
      });
      return;
    }

    try {
      setReviewSubmittingOrderId(order.id);

      const payload = new FormData();
      payload.append("orderId", String(order.id));
      payload.append("productId", String(order.items[0].id));
      payload.append("buyerEmail", buyerEmail);
      payload.append("rating", String(form.rating));
      payload.append("comment", form.comment);

      form.photos.forEach((file) => payload.append("photos", file));
      form.videos.forEach((file) => payload.append("videos", file));

      const response = await axios.post(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/reviews`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        showModal({
          type: "success",
          title: "success",
          message: "Review submitted successfully.",
        });

        setReviewForms((prev) => ({
          ...prev,
          [order.orderId]: getDefaultReviewForm(),
        }));

        setExpandedReviewOrderId(null);
        await fetchOrders();
      } else {
        showModal({
          type: "success",
          title: "success",
          message: response.data?.message || "Unable to submit review.",
        });
      }
    } catch (error: any) {
      showModal({
        type: "error",
        title: "error",
        message: error?.response?.data?.message || "Unable to submit review.",
      });
    } finally {
      setReviewSubmittingOrderId(null);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this order?",
    );
    if (!confirmed) return;

    try {
      setCancelLoadingOrderId(orderId);

      const response = await axios.patch(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/orders/${encodeURIComponent(orderId)}/cancel`,
      );

      if (response.data?.success) {
        await fetchOrders();
      } else {
        showModal({
          type: "success",
          title: "Success",
          message: response.data?.message || "Unable to cancel order.",
        });
      }
    } catch (error: any) {
      showModal({
        type: "error",
        title: "error",
        message: error?.data?.message || "Unable to cancel order.",
      });
    } finally {
      setCancelLoadingOrderId(null);
    }
  };

  const tabs: { key: OrderTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "to_pay", label: "To Pay" },
    { key: "to_ship", label: "To Ship" },
    { key: "to_receive", label: "To Receive" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
    { key: "return_refund", label: "Return Refund" },
  ];

  const filteredOrders = useMemo(() => {
    if (activeTab === "all") return orders;
    return orders.filter((order: any) => order.status === activeTab);
  }, [activeTab, orders]);

  const getTabCount = (tabKey: OrderTab) => {
    if (tabKey === "all") return orders.length;
    return orders.filter((order: any) => order.status === tabKey).length;
  };

  const formatCurrency = (value: number) =>
    `RM${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getProductImage = (item: OrderItem) => {
    if (item.image_path) {
      return `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${item.id}/${item.image_path}`;
    }
    return "/placeholder-product.png";
  };

  const getStatusLabel = (status: OrderTab) => {
    switch (status) {
      case "to_pay":
        return "TO PAY";
      case "to_ship":
        return "TO SHIP";
      case "to_receive":
        return "TO RECEIVE";
      case "completed":
        return "COMPLETED";
      case "cancelled":
        return "CANCELLED";
      case "return_refund":
        return "RETURN / REFUND";
      default:
        return "ALL";
    }
  };

  if (loading) {
    return (
      <section className="orders-page">
        <div className="orders-container">
          <div className="orders-empty">Loading orders...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="orders-page">
      <div className="orders-container">
        <div className="orders-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`orders-tab-btn ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label} ({getTabCount(tab.key)})
            </button>
          ))}
        </div>

        <div className="orders-list">
          {filteredOrders.length === 0 ? (
            <div className="orders-empty">No orders found.</div>
          ) : (
            filteredOrders.map((order: any) => {
              const firstItem = order.items?.[0];
              if (!firstItem) return null;

              const reviewForm =
                reviewForms[order.orderId] || getDefaultReviewForm();
              const isReviewExpanded = expandedReviewOrderId === order.orderId;

              return (
                <div className="orders-card" key={order.orderId}>
                  <div className="orders-card-top">
                    <div className="orders-card-left">
                      <span className="orders-store-name">
                        LyraZone | {order.orderId}
                      </span>
                    </div>

                    <div
                      className={`orders-status-badge status-${order.status}`}
                    >
                      {getStatusLabel(order.status)}
                    </div>
                  </div>

                  <div
                    className="orders-product-row"
                    onClick={() => navigate(`/orders/${order.orderId}`)}
                  >
                    <div className="orders-product-image-wrap">
                      <img
                        src={getProductImage(firstItem)}
                        alt={firstItem.productName}
                        className="orders-product-image"
                      />
                    </div>

                    <div className="orders-product-info">
                      <h4>{firstItem.productName}</h4>
                      <p>Variation: {firstItem.variant || "Default"}</p>
                      <span>x{firstItem.quantity}</span>
                      <span>{new Date(order.createdAt).toLocaleString()}</span>
                    </div>

                    <div className="orders-product-price-box">
                      <span className="orders-product-price">
                        {formatCurrency(firstItem.unitPrice)}
                      </span>
                    </div>
                  </div>

                  <div className="orders-card-bottom">
                    <div className="orders-total">
                      {order.status === "completed"
                        ? "Order Total:"
                        : "Amount Payable:"}{" "}
                      <strong>{formatCurrency(order.totalAmount)}</strong>
                    </div>

                    <div className="orders-actions">
                      {(order.status === "to_pay" ||
                        order.status === "to_ship") && (
                        <>
                          <button
                            type="button"
                            className="orders-btn orders-btn-muted"
                          >
                            {order.status === "to_pay"
                              ? "Pending"
                              : "Processing"}
                          </button>

                          <button
                            type="button"
                            className="orders-btn orders-btn-outline"
                            onClick={() => navigate("/support")}
                          >
                            Contact Us
                          </button>

                          {canCancelOrder(order) && (
                            <button
                              type="button"
                              className="orders-btn orders-btn-outline"
                              onClick={() => handleCancelOrder(order.orderId)}
                              disabled={cancelLoadingOrderId === order.orderId}
                            >
                              {cancelLoadingOrderId === order.orderId
                                ? "Cancelling..."
                                : "Cancel Order"}
                            </button>
                          )}
                        </>
                      )}

                      {order.status === "completed" && (
                        <>
                          <button
                            type="button"
                            className="orders-btn orders-btn-primary"
                            onClick={() => handleBuyAgain(order)}
                          >
                            Buy Again
                          </button>

                          {!order.hasReview ? (
                            <button
                              type="button"
                              className="orders-btn orders-btn-primary"
                              onClick={() => handleReview(order)}
                            >
                              {isReviewExpanded
                                ? "Hide Review"
                                : "Leave a Review"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="orders-btn orders-btn-muted"
                              disabled
                            >
                              Reviewed
                            </button>
                          )}

                          <button
                            type="button"
                            className="orders-btn orders-btn-outline orders-btn-invoice"
                            onClick={() =>
                              window.open(
                                `${
                                  import.meta.env.VITE_LYRAZONE_BASE_URL ||
                                  "http://localhost:5000/api"
                                }/orders/${encodeURIComponent(order.orderId)}/invoice`,
                                "_blank",
                              )
                            }
                          >
                            Get Invoice
                          </button>

                          <button
                            type="button"
                            className="orders-btn orders-btn-outline"
                            onClick={() => navigate("/support")}
                          >
                            Contact Us
                          </button>
                        </>
                      )}

                      {order.status !== "to_pay" &&
                        order.status !== "to_ship" &&
                        order.status !== "completed" && (
                          <button
                            type="button"
                            className="orders-btn orders-btn-outline"
                            onClick={() => navigate("/support")}
                          >
                            Contact Us
                          </button>
                        )}
                    </div>
                  </div>

                  {order.status === "completed" && isReviewExpanded && (
                    <div
                      className="orders-review-panel"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="orders-review-header">
                        <button
                          type="button"
                          className="orders-review-back"
                          onClick={() => setExpandedReviewOrderId(null)}
                        >
                          ←
                        </button>
                        <h3>Leave a review</h3>
                      </div>

                      <div className="orders-review-product">
                        <div className="orders-review-product-image-wrap">
                          <img
                            src={getProductImage(firstItem)}
                            alt={firstItem.productName}
                            className="orders-review-product-image"
                          />
                        </div>

                        <div className="orders-review-product-info">
                          <h4>{firstItem.productName}</h4>
                          <p>Variation: Default</p>
                        </div>
                      </div>

                      <div className="orders-review-upload-row">
                        <label className="orders-upload-card">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={(e) =>
                              handlePhotoChange(order.orderId, e.target.files)
                            }
                          />
                          <span className="orders-upload-icon">🖼️</span>
                          <span className="orders-upload-title">Photo</span>
                          <small>
                            {reviewForm.photos.length > 0
                              ? `${reviewForm.photos.length} selected`
                              : "Add photos"}
                          </small>
                        </label>

                        <label className="orders-upload-card">
                          <input
                            type="file"
                            accept="video/*"
                            multiple
                            hidden
                            onChange={(e) =>
                              handleVideoChange(order.orderId, e.target.files)
                            }
                          />
                          <span className="orders-upload-icon">🎥</span>
                          <span className="orders-upload-title">Video</span>
                          <small>
                            {reviewForm.videos.length > 0
                              ? `${reviewForm.videos.length} selected`
                              : "Add videos"}
                          </small>
                        </label>
                      </div>

                      <div className="orders-review-text-wrap">
                        <textarea
                          className="orders-review-textarea"
                          placeholder="Sharing your thought and experience about this item."
                          maxLength={3000}
                          value={reviewForm.comment}
                          onChange={(e) =>
                            updateReviewForm(
                              order.orderId,
                              "comment",
                              e.target.value,
                            )
                          }
                        />
                        <div className="orders-review-counter">
                          {reviewForm.comment.length}/3000
                        </div>
                      </div>

                      <div className="orders-review-rating-row">
                        <span className="orders-review-rating-label">
                          * Rating
                        </span>

                        <div className="orders-stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className={`orders-star-btn ${
                                reviewForm.rating >= star ? "active" : ""
                              }`}
                              onClick={() =>
                                updateReviewForm(order.orderId, "rating", star)
                              }
                            >
                              ★
                            </button>
                          ))}
                        </div>

                        <span className="orders-rating-text">
                          {reviewForm.rating === 5
                            ? "Excellent"
                            : reviewForm.rating === 4
                              ? "Very Good"
                              : reviewForm.rating === 3
                                ? "Good"
                                : reviewForm.rating === 2
                                  ? "Fair"
                                  : "Poor"}
                        </span>
                      </div>

                      <p className="orders-review-guideline">
                        Please follow the <span>review guidelines</span> when
                        writing reviews.
                      </p>

                      <div className="orders-review-submit-wrap">
                        <button
                          type="button"
                          className="orders-review-submit-btn"
                          onClick={() => handleSubmitReview(order)}
                          disabled={reviewSubmittingOrderId === order.id}
                        >
                          {reviewSubmittingOrderId === order.id
                            ? "Submitting..."
                            : "Submit"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default Orders;
